import { Segmented } from '@/components/ui/segmented';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { getWards, getAdmissions, type Ward, type AdmissionRow } from '@/lib/ipdApi';
import { Bed as BedIcon, Search, Plus, Settings, Receipt, Printer, Paperclip, ListFilter, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { format, subDays } from 'date-fns';

const DEFAULT_RANGE_DAYS = 30;
const defaultDateFrom = () => format(subDays(new Date(), DEFAULT_RANGE_DAYS), 'yyyy-MM-dd');
import AdmitPatientModal from '@/components/IPD/AdmitPatientModal';
import AdmissionPeekPanel from '@/components/IPD/AdmissionPeekPanel';
import { admissionStatus, bedLabel } from '@/components/IPD/admissionStatus';
import { StatusChip } from '@/components/ui/status-chip';
import { formatDate } from '@/lib/formatDate';
import { usePageTitle } from "@/hooks/usePageTitle";

// ─── Select helper ─────────────────────────────────────────────────────────────

function Sel({ value, onChange, children, disabled }: {
  value: string; onChange: (v: string) => void;
  children: React.ReactNode; disabled?: boolean;
}) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
      className="h-8 w-full rounded-md border border-field bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50">
      {children}
    </select>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

// Actions column is pinned right so it stays reachable when the table scrolls sideways (below ~1280px).
// Pinned cells need an opaque background, so the row tints (header, hover, selected) are mixed onto the card colour.
const PINNED_EDGE     = 'sticky right-0 z-10 max-xl:shadow-[-6px_0_6px_-6px_rgb(0_0_0/0.15)]';
const PINNED_HEAD     = `${PINNED_EDGE} bg-[color-mix(in_srgb,hsl(var(--muted))_40%,hsl(var(--card)))]`;
const PINNED_CELL     = `${PINNED_EDGE} group-hover:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,hsl(var(--card)))]`;
const PINNED_SELECTED = 'bg-[color-mix(in_srgb,hsl(var(--primary))_5%,hsl(var(--card)))]';

const rupees = (n: number | null | undefined) => `₹${Number(n ?? 0).toLocaleString('en-IN')}`;

export default function IPDPage() {
  usePageTitle('IPD');
  const { currentUser } = useAuth();
  const { can, isOwner, hasFeature } = usePermission();
  const navigate = useNavigate();
  const canWrite = can('ipd:write');
  const canManageWards = isOwner || currentUser?.role === 'admin';

  const [admissions, setAdmissions] = useState<AdmissionRow[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'admitted' | 'all'>('admitted');
  const [wardFilter, setWardFilter] = useState('');
  // Row → side panel; the full admission page is one click further
  const [peek, setPeek] = useState<AdmissionRow | null>(null);
  const columns: { label: string; numeric?: boolean }[] = [
    { label: 'Adm. No.' }, { label: 'Patient' }, { label: 'Ward / Bed' }, { label: 'Doctor' }, { label: 'Admitted' },
    ...(statusFilter === 'all' ? [{ label: 'Discharged' }] : []),
    { label: 'Total', numeric: true }, { label: 'Paid', numeric: true }, { label: 'Due', numeric: true }, { label: 'Advance', numeric: true },
    { label: 'Status' }, { label: '' },
  ];
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'due'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [wards, setWards] = useState<Ward[]>([]);
  const [admitOpen,  setAdmitOpen]  = useState(false);

  // Debounce search — used to decide whether to widen the "All" date range server-side
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // A non-empty search looks for a specific patient/admission across all history,
  // so it overrides the default "last 30 days" window for the "All" view.
  const dateFilterActive = statusFilter === 'all' && !debouncedSearch.trim();

  const load = useCallback(() => {
    if (!currentUser?.clinic_id) return;
    setLoading(true);
    getAdmissions(currentUser.clinic_id, {
      status: statusFilter,
      dateFrom: dateFilterActive ? dateFrom || undefined : undefined,
      dateTo:   dateFilterActive ? dateTo   || undefined : undefined,
      search:   debouncedSearch.trim() || undefined,
    })
      .then(data => setAdmissions(data ?? []))
      .catch(err => {
        console.error(err);
        toast({ title: 'Error', description: 'Failed to load admissions', variant: 'destructive' });
      })
      .finally(() => setLoading(false));
  }, [currentUser?.clinic_id, statusFilter, dateFilterActive, dateFrom, dateTo, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!currentUser?.clinic_id) return;
    getWards(currentUser.clinic_id).then(w => setWards(w ?? [])).catch(console.error);
  }, [currentUser?.clinic_id]);

  if (!hasFeature('ipd')) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <BedIcon className="w-8 h-8 text-muted-foreground mb-2" />
        <h2 className="text-base font-semibold mb-1">IPD Not Available</h2>
        <p className="text-sm text-muted-foreground max-w-sm">
          In-patient department management is not included in your current plan. Contact support to upgrade.
        </p>
      </div>
    );
  }

  const activeFilterCount =
    (wardFilter ? 1 : 0) +
    (statusFilter === 'all' && paymentFilter === 'due' ? 1 : 0) +
    (statusFilter === 'all' && dateFrom !== defaultDateFrom() ? 1 : 0) +
    (statusFilter === 'all' && dateTo ? 1 : 0);

  const filtered = admissions.filter(a => {
    if (wardFilter && a.ward_id !== wardFilter) return false;
    if (paymentFilter === 'due' && !(Number(a.balance_due) > 0)) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      a.patient_name?.toLowerCase().includes(q) ||
      a.patient_mrn?.toLowerCase().includes(q)  ||
      a.admission_number?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="flex flex-col h-full">

      {/* Header — wraps on phones */}
      <PageHeader
        icon={BedIcon}
        title="IPD"
        className="px-4 sm:px-6 pt-4 pb-3 border-b shrink-0"
        actions={<>
          {/* Status filter */}
          <Segmented
            aria-label="Admissions"
            options={[{ value: 'admitted', label: 'Current' }, { value: 'all', label: 'All' }]}
            value={statusFilter}
            onChange={s => {
              setStatusFilter(s);
              if (s === 'admitted') {
                setPaymentFilter('all');
                setDateFrom('');
                setDateTo('');
              } else if (s === 'all' && !dateFrom && !dateTo) {
                setDateFrom(defaultDateFrom());
              }
            }}
          />
          {/* Filters popover: Ward, Payment, Date range */}
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 text-xs relative">
                <ListFilter className="w-3.5 h-3.5 mr-1" /> Filters
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-2xs font-medium text-primary-foreground">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 space-y-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Ward</label>
                <Sel value={wardFilter} onChange={setWardFilter}>
                  <option value="">All wards</option>
                  {wards.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </Sel>
              </div>

              {/* Payment + date range — only meaningful once discharged/invoiced admissions are in view */}
              {statusFilter === 'all' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Payment</label>
                    <Sel value={paymentFilter} onChange={v => setPaymentFilter(v as 'all' | 'due')}>
                      <option value="all">All payments</option>
                      <option value="due">Due only</option>
                    </Sel>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Admission Date Range</label>
                    <div className="space-y-2">
                      <Input
                        type="date"
                        value={dateFrom}
                        onChange={e => setDateFrom(e.target.value)}
                        className="h-8 text-sm w-full"
                      />
                      <Input
                        type="date"
                        value={dateTo}
                        onChange={e => setDateTo(e.target.value)}
                        className="h-8 text-sm w-full"
                      />
                    </div>
                  </div>
                </>
              )}

              {activeFilterCount > 0 && (
                <Button
                  size="sm" variant="ghost" className="h-7 w-full text-xs text-muted-foreground"
                  onClick={() => { setWardFilter(''); setPaymentFilter('all'); setDateFrom(statusFilter === 'all' ? defaultDateFrom() : ''); setDateTo(''); }}
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Clear filters
                </Button>
              )}
            </PopoverContent>
          </Popover>
          {statusFilter === 'all' && !search.trim() && dateFrom && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              Showing {dateTo
                ? `${format(new Date(dateFrom), 'dd MMM')} – ${format(new Date(dateTo), 'dd MMM')}`
                : `since ${format(new Date(dateFrom), 'dd MMM yyyy')}`}
            </span>
          )}
          {/* Search */}
          <div className="relative flex-1 min-w-[10rem] sm:flex-none">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search patient, MRN…"
              className="h-8 pl-8 text-sm w-full sm:w-52"
            />
          </div>
          {/* Wards settings */}
          {canManageWards && (
            <Button size="sm" variant="outline" onClick={() => navigate('/ipd/settings')} title="Wards & Beds">
              <Settings className="w-4 h-4" />
            </Button>
          )}
          {/* Admit button */}
          {canWrite && (
            <Button size="sm" onClick={() => setAdmitOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Admit patient
            </Button>
          )}
        </>}
      />

      {/* List */}
      <div className="flex-1 overflow-auto px-4 sm:px-6 py-4">
        {loading ? (
          <div className="rounded-lg border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="h-8 border-b border-border bg-muted/40">
                  {columns.map(c => (
                    <th key={c.label || 'actions'} className={cn('px-3 text-2xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap', c.numeric ? 'text-right' : 'text-left')}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="h-10 border-b border-divider last:border-0">
                    {columns.map((_, j) => (
                      <td key={j} className="px-3">
                        <div className="h-4 rounded bg-muted animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-muted-foreground py-12 text-center">
            {search ? 'No matching admissions.' : 'No admissions found.'}
          </div>
        ) : (
          <>
          {/* Below 1280px (phones, tablets): one row per admission — the full table only fits from xl. Tap opens the side panel */}
          <ul className="xl:hidden rounded-lg border border-border bg-card divide-y divide-border overflow-hidden">
            {filtered.map(a => {
              const status = admissionStatus(a.status);
              const due = a.total_amount != null ? Number(a.balance_due ?? 0) : 0;
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => setPeek(a)}
                    className="w-full text-left px-4 py-3 active:bg-muted/60 focus-visible:outline-none focus-visible:bg-muted/60"
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-foreground truncate capitalize">{a.patient_name}</span>
                      <span className="flex items-center gap-1 shrink-0">
                        {!!a.is_mlc && <StatusChip tone="alert">MLC</StatusChip>}
                        {a.status === 'admitted' && a.discharge_advised_at
                          ? <StatusChip tone="warning">Discharge advised</StatusChip>
                          : <StatusChip tone={status.tone}>{status.label}</StatusChip>}
                      </span>
                    </span>
                    <span className="block text-xs text-muted-foreground truncate">
                      {a.ward_name} · {bedLabel(a.bed_number)} · {a.admission_date ? formatDate(a.admission_date) : '—'}
                    </span>
                    <span className="flex items-center justify-between gap-2 text-xs">
                      <span className="font-mono text-muted-foreground">{a.admission_number}</span>
                      {due > 0 && <span className="font-mono font-medium tabular-nums text-highlight-foreground">{rupees(due)} due</span>}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="hidden xl:block rounded-lg border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="h-8 border-b border-border bg-muted/40">
                  {columns.map(c => (
                    <th key={c.label || 'actions'} scope="col" className={cn(
                      'px-3 text-2xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap',
                      c.numeric ? 'text-right' : 'text-left',
                      c.label === '' && PINNED_HEAD,
                    )}>
                      {c.label || <span className="sr-only">Actions</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => {
                  const status = admissionStatus(a.status);
                  const billed = a.total_amount != null;
                  const selected = peek?.id === a.id;
                  return (
                  <tr
                    key={a.id}
                    tabIndex={0}
                    aria-selected={selected}
                    onClick={() => setPeek(a)}
                    onKeyDown={e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setPeek(a); } }}
                    className={cn(
                      'group h-10 border-b border-divider last:border-0 cursor-pointer transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:bg-muted/60',
                      selected && 'bg-primary/5',
                    )}
                  >
                    <td className="px-3 whitespace-nowrap font-mono text-xs text-foreground/80">{a.admission_number}</td>
                    <td className="px-3 whitespace-nowrap">
                      <span className="font-medium text-foreground capitalize">{a.patient_name}</span>
                      {a.patient_mrn && <span className="ml-2 font-mono text-xs text-muted-foreground">{a.patient_mrn}</span>}
                    </td>
                    <td className="px-3 whitespace-nowrap text-xs text-foreground">
                      {a.ward_name} <span className="text-muted-foreground">· {bedLabel(a.bed_number)}</span>
                    </td>
                    <td className="px-3 whitespace-nowrap text-xs text-foreground">{a.admitting_doctor_name}</td>
                    <td className="px-3 whitespace-nowrap text-xs text-muted-foreground tabular-nums">
                      {a.admission_date ? formatDate(a.admission_date) : '—'}
                    </td>
                    {statusFilter === 'all' && (
                      <td className="px-3 whitespace-nowrap text-xs text-muted-foreground tabular-nums">
                        {a.discharge_date ? formatDate(a.discharge_date) : '—'}
                      </td>
                    )}
                    <td className="px-3 whitespace-nowrap text-right font-mono tabular-nums">{billed ? rupees(a.total_amount) : '—'}</td>
                    <td className="px-3 whitespace-nowrap text-right font-mono tabular-nums">{billed ? rupees(a.paid_amount) : '—'}</td>
                    <td className={cn('px-3 whitespace-nowrap text-right font-mono tabular-nums', billed && Number(a.balance_due) > 0 ? 'text-highlight-foreground font-medium' : 'text-muted-foreground')}>
                      {billed ? rupees(a.balance_due) : '—'}
                    </td>
                    <td className="px-3 whitespace-nowrap text-right font-mono tabular-nums text-muted-foreground">
                      {Number(a.available_advance) > 0 ? <span className="text-foreground">{rupees(a.available_advance)}</span> : '—'}
                    </td>
                    <td className="px-3 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        {a.status === 'admitted' && a.discharge_advised_at
                          ? <StatusChip tone="warning">Discharge advised</StatusChip>
                          : <StatusChip tone={status.tone}>{status.label}</StatusChip>}
                        {!!a.is_mlc && <StatusChip tone="alert">MLC</StatusChip>}
                      </span>
                    </td>
                    <td className={cn('px-3 whitespace-nowrap bg-card', PINNED_CELL, selected && PINNED_SELECTED)}>
                      <div className="flex items-center gap-0.5">
                        <Button
                          variant="ghost" size="icon" className="h-8 w-8"
                          title="Billing" aria-label={`Billing for ${a.patient_name}`}
                          onClick={e => { e.stopPropagation(); navigate(`/ipd/admissions/${a.id}?tab=charges`); }}
                        >
                          <Receipt className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" className="h-8 w-8"
                          title="Discharge Summary" aria-label={`Discharge summary for ${a.patient_name}`}
                          onClick={e => { e.stopPropagation(); navigate(`/ipd/admissions/${a.id}?tab=discharge`); }}
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" className="h-8 w-8"
                          title="Attachments" aria-label={`Attachments for ${a.patient_name}`}
                          onClick={e => { e.stopPropagation(); navigate(`/ipd/admissions/${a.id}?tab=attachments`); }}
                        >
                          <Paperclip className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      <AdmissionPeekPanel admission={peek} onClose={() => setPeek(null)} />

      {/* Admit Patient Modal */}
      {currentUser?.clinic_id && (
        <AdmitPatientModal
          open={admitOpen}
          onClose={() => setAdmitOpen(false)}
          clinicId={currentUser.clinic_id}
          onAdmitted={id => {
            setAdmitOpen(false);
            navigate(`/ipd/admissions/${id}`);
          }}
        />
      )}
    </div>
  );
}
